// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.
import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle as MuiDialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { AppConfig } from "../../../configs/admin-config";
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { getAccessToken } from "../../../utils/oauth";

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "white",
  },
  input: {
    marginLeft: theme.spacing(1),
    flex: 1,
  },
  iconButton: {
    padding: 10,
  },
  divider: {
    height: 28,
    margin: 4,
  },
  tableContainer: {
    marginTop: 20,
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: theme.palette.primary.light,
  },
  column: {
    cursor: "pointer",
    "&:hover": {
      fontWeight: 600,
    },
  },
  tableRow1: {
    "&:hover": {
      backgroundColor: "#F5F5F5",
      cursor: "pointer",
    },
  },
  tableRow2: {
    "&:hover": {
      cursor: "not-allowed",
    },
  },
  primaryText: {
    color: theme.palette.primary.main,
    fontSize: 12,
    fontWeight: "bold",
  },
  secondaryText: {
    color: theme.palette.secondary.main,
    fontSize: 12,
    fontWeight: "bold",
  },
  button: {
    backgroundColor: "white",
    textTransform: "none",
    fontSize: 11,
  },
  addButton: {
    textTransform: "none",
  },
  dialog: {
    border: "1px solid black",
  },
  dialogActions: {
    paddingRight: 23,
    paddingTop: 15,
    paddingBottom: 20,
  },
  msgTypo: {
    paddingTop: 20,
    textAlign: "center",
    color: theme.palette.secondary.main,
  },
  infoMsg: {
    marginTop: 10,
    color: "grey",
  },
  errorMsg: {
    color: "red",
    fontSize: 12,
    marginBottom: 5,
  },
}));

const EditResellerDialog = (props) => {
  const { classes } = useStyles();
  const [companyName, setCompanyName] = useState(props.companyName || "");
  const [companyNameError, setCompanyNameError] = useState(false);
  const [emails, setEmails] = useState(props.emails || [""]);
  const [contactEmailError, setContactEmailError] = useState(false);
  const [channelManagerEmailError, setChannelManagerEmailError] =
    useState(false);
  const [channelManagerEmail, setChannelManagerEmail] = useState(
    props.channelManagerEmail || ""
  );
  const [contactName, setContactName] = useState(props.contactName || "");
  const [contactNameError, setContactNameError] = useState(false);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidEmail2, setInvalidEmail2] = useState(false);
  const [formRender, setFormRender] = useState(0);
  const [snack, setSnackBar] = useState({
    visibility: false,
    serverity: "",
    message: "",
  });
  const [backdropOpen, setBackdropOpen] = useState(false);

  const DialogTitle = ({ children, onClose, data, ...other }) => (
    <MuiDialogTitle sx={{ margin: 0, padding: 2 }} {...other}>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Grid item xs>
          <Typography variant="h4">{children}</Typography>
        </Grid>
        <Grid item>
          {handleDialogClose ? (
            <IconButton aria-label="close" sx={{ color: "grey.500" }} onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          ) : null}
        </Grid>
      </Grid>
    </MuiDialogTitle>
  );

  const companyNameHandler = (e) => {
    setCompanyNameError(false);
    setCompanyName(e.target.value);
  };

  const contactNameHandler = (e) => {
    setContactNameError(false);
    setContactName(e.target.value);
  };

  const channelManagerEmailHandler = (e) => {
    setChannelManagerEmailError(false);
    setInvalidEmail2(false);
    setChannelManagerEmail(e.target.value);
  };

  const handleDialogClose = () => {
    props.handleDialogClose(false);
  };

  const handleAddEmail = () => {
    setEmails([...emails, ""]);
  };

  const handleEmailChange = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const handleRemoveEmail = (index) => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails);
  };

  const validateForm = () => {
    let result = true;
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (companyName === "") {
      result = false;
      setCompanyNameError(true);
    }
    if (contactName === "") {
      result = false;
      setContactNameError(true);
    }
    if (emails.some((email) => email === "")) {
      result = false;
      setContactEmailError(true);
    }
    if (emails.some((email) => !re.test(String(email).toLowerCase()))) {
      result = false;
      setInvalidEmail(true);
    }
    if (channelManagerEmail === "") {
      result = false;
      setChannelManagerEmailError(true);
    }
    if (
      channelManagerEmail !== "" &&
      !re.test(String(channelManagerEmail).toLowerCase())
    ) {
      result = false;
      setInvalidEmail2(true);
    }
    return result;
  };

  const saveEditedCompany = async (linkId) => {
    setBackdropOpen(true);
    if (validateForm()) {
      const requestURL = AppConfig.baseUrl + AppConfig.partnerData + "links/" + linkId + "/" + AppConfig.resendEmail;
      let data = {
        emails: emails,
        companyName: companyName,
        contactName: contactName,
        channelManagerEmail: channelManagerEmail,
        clientUrl: AppConfig.externalUrl,
      };
      try {
        let response = await fetch(requestURL, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + getAccessToken(),
          },
          body: JSON.stringify(data),
        });
        let body = await response.json();
        setBackdropOpen(false);
        if (response.status === 200) {
          setCompanyName("");
          setEmails([""]);
          setContactName("");
          setChannelManagerEmail("");
          props.setSnackBar({
            visibility: true,
            severity: "success",
            message: "Success",
          });
          props.setFormRender(formRender + 1);
          handleDialogClose();
        } else {
          console.error(body.message);
          setSnackBar({
            visibility: true,
            severity: "error",
            message: "Failed",
          });
        }
        return null;
      } catch (error) {
        console.error(error);
        return error;
      }
    }
  };

  useEffect(() => {}, [props.dialogOpen]);

  return (
    <>
      <Backdrop open={backdropOpen} className={classes.backdrop}>
        <CircularProgress color="secondary" />
      </Backdrop>
      <Dialog open={props.dialogOpen} fullWidth className={classes.dialog}>
        <DialogTitle id="form-dialog-title">Reseller Details</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <DialogContentText>
            The following details may be edited
          </DialogContentText>
          <TextField
            autoFocus
            required
            defaultValue={companyName}
            margin="dense"
            label="Partner Company Name"
            type="email"
            variant="outlined"
            fullWidth
            onChange={companyNameHandler}
            error={companyNameError}
          />
          {companyNameError && (
            <Typography variant="body2" className={classes.errorMsg}>
              Please enter Company Name
            </Typography>
          )}

          <TextField
            defaultValue={contactName}
            required
            margin="dense"
            label="Partner Contact Name"
            variant="outlined"
            fullWidth
            onChange={contactNameHandler}
            error={contactNameError}
          />
          {contactNameError && (
            <Typography variant="body2" className={classes.errorMsg}>
              Please enter Contact Name
            </Typography>
          )}

          {emails.map((email, index) => (
            <Grid container alignItems="center" spacing={1} key={index}>
              <Grid item xs={11}>
                <TextField
                  required
                  margin="dense"
                  variant="outlined"
                  label={`Contact Email ${index + 1}`}
                  fullWidth
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  error={contactEmailError || invalidEmail}
                />
              </Grid>
              <Grid item xs={1}>
                {emails.length > 1 && (
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => handleRemoveEmail(index)}
                    aria-label="remove email"
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}

          <Grid item xs={11} style={{ display: "flex" }}>
            <IconButton
              className={classes.iconButton}
              onClick={handleAddEmail}
              aria-label="add email"
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </Grid>

          {invalidEmail && (
            <Typography variant="body2" className={classes.errorMsg}>
              Please enter a valid Email
            </Typography>
          )}

          <TextField
            required
            defaultValue={channelManagerEmail}
            margin="dense"
            variant="outlined"
            label="Channel Manager Email"
            fullWidth
            onChange={channelManagerEmailHandler}
            error={channelManagerEmailError || invalidEmail2}
          />
          {channelManagerEmailError && (
            <Typography variant="body2" className={classes.errorMsg}>
              Please enter Channel Manager Email
            </Typography>
          )}

          {invalidEmail2 && (
            <Typography variant="body2" className={classes.errorMsg}>
              Please enter a valid Email
            </Typography>
          )}
          <Typography variant="body2" className={classes.infoMsg}>
            Partner form link will be automatically sent to partner contact
            email and channel manager will receive a confirmation email.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            className={classes.addButton}
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => saveEditedCompany(props.linkId)}
          >
            Save Edit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditResellerDialog;
